import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/auth/useAuth';
import { useZendeskJwt } from '../../hooks/api/useZendeskJwt';
import { env } from '../../env';

const ZENDESK_BASE_URL = env.ZENDESK_BASE_URL || '';

function ZendeskSSO() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return_to') || ZENDESK_BASE_URL;
  const formRef = useRef<HTMLFormElement>(null);
  const [formData, setFormData] = useState<{
    action: string;
    jwt: string;
    returnTo: string;
  } | null>(null);
  const { data, isError } = useZendeskJwt();

  useEffect(() => {
    if (!isAuthenticated) {
      const target = `/auth/zendesk-sso?return_to=${encodeURIComponent(returnTo)}`;
      navigate(`/welcome?returnTo=${encodeURIComponent(target)}`, {
        replace: true,
      });
    }
  }, [isAuthenticated, navigate, returnTo]);

  useEffect(() => {
    if (data) {
      setFormData({
        action: data.action,
        jwt: data.jwt,
        returnTo: returnTo,
      });
    }
  }, [data]);

  useEffect(() => {
    if (formData && formRef.current) {
      formRef.current.submit();
    }
  }, [formData]);

  if (isError) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="200px"
      >
        <Typography color="error">
          {t('auth.zendesk.error')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="200px"
        gap={2}
      >
        <CircularProgress />
        <Typography>{t('auth.zendesk.redirecting')}</Typography>
      </Box>

      {formData && (
        <form
          ref={formRef}
          method="POST"
          action={formData.action}
          style={{ display: 'none' }}
        >
          <input type="hidden" name="jwt" value={formData.jwt} />
          {formData.returnTo && (
            <input type="hidden" name="return_to" value={formData.returnTo} />
          )}
        </form>
      )}
    </>
  );
}

export default ZendeskSSO;
